import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  ToggleButton,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Close, InfoOutline } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { createDatasetSchedule } from '../../api/dataset';
import { fontSize } from '../..';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';
import { isNameAllowed } from '../CommonFunctions/CommonFunctions';
import { IDatasetSchedule } from '../../api/types/dataset';

interface CreateScheduleProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedDataset: { workspaceId: string; datasetId: string };
  fetchDatasetSchedule: () => void;
  selectedSchedule: IDatasetSchedule | null;
  setSelectedSchedule: React.Dispatch<React.SetStateAction<IDatasetSchedule | null>>;
  setViewSchedule: React.Dispatch<React.SetStateAction<boolean>>;
  viewSchedule: boolean;
  schedules: IDatasetSchedule[];
}

const hours = Array.from({ length: 12 }, (_, i) => i + 1);
const everyhour = [1, 2, 3, 4, 6, 8, 12];
const minutes = Array.from({ length: 60 }, (_, i) => (i < 10 ? '0' : '') + i);
const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Last'];

const selectStyle = {
  minWidth: 80,
  height: 36,
  '&.MuiSelect-root': {
    '&.MuiSelect-select': {
      height: 36,
    },
  },
  '@media (max-width:600px)': {
    minWidth: 70,
  },
};

export default function CreateSchedule({
  open,
  setOpen,
  selectedDataset,
  fetchDatasetSchedule,
  selectedSchedule,
  setSelectedSchedule,
  setViewSchedule,
  viewSchedule,
  schedules,
}: CreateScheduleProps) {
  const [expanded, setExpanded] = useState<string | false>('daily');
  const [activeSection, setActiveSection] = useState<string>('daily');

  const [scheduledName, setScheduledName] = useState<string>('');
  const [minutesState, setMinutesState] = useState<{
    startMinute: string;
    calTime: string;
    every: string;
    from: { hour: string; minute: string; ampm: string };
    to: { hour: string; minute: string; ampm: string };
    days: string[];
  }>({
    startMinute: '00',
    calTime: '00',
    every: '30',
    from: { hour: '10', minute: '00', ampm: 'AM' },
    to: { hour: '6', minute: '00', ampm: 'PM' },
    days: [],
  });
  const [hourlyState, setHourlyState] = useState<{
    startMinute: string;
    every: string;
    from: { hour: string; minute: string; ampm: string };
    to: { hour: string; minute: string; ampm: string };
    days: string[];
  }>({
    startMinute: '00',
    every: '2',
    from: { hour: '10', minute: '00', ampm: 'AM' },
    to: { hour: '6', minute: '00', ampm: 'PM' },
    days: [],
  });
  const [dailyState, setDailyState] = useState<{
    at: { hour: string; minute: string; ampm: string };
    days: string[];
  }>({
    at: { hour: '8', minute: '30', ampm: 'AM' },
    days: [],
  });
  const [weeklyState, setWeeklyState] = useState<{
    at: { hour: string; minute: string; ampm: string };
    days: string[];
    alternateWeeks: boolean;
  }>({
    at: { hour: '8', minute: '30', ampm: 'AM' },
    days: [],
    alternateWeeks: false,
  });
  const [monthlyState, setMonthlyState] = useState<{
    at: { hour: string; minute: string; ampm: string };
    mode: 'day' | 'nthWeekday';
    day: string;
    nth: string;
    weekday: string;
    everyNMonths: string;
  }>({
    at: { hour: '8', minute: '30', ampm: 'AM' },
    mode: 'day',
    day: '1',
    nth: 'First',
    weekday: 'Mon',
    everyNMonths: '1',
  });

  const [alert, setAlert] = useState<{
    severity: 'success' | 'info' | 'warning' | 'error';
    message: string;
    show: boolean;
  } | null>(null);

  const timezoneDisplay = useMemo(() => {
    const date = new Date();
    const systemOptions = new Intl.DateTimeFormat().resolvedOptions();
    const gmtPart =
      new Intl.DateTimeFormat(systemOptions.locale, { timeZoneName: 'short' })
        .format(date)
        .split(',')[1]
        ?.trim() || '';
    const tzName = systemOptions.timeZone;
    return `${gmtPart} ${tzName}`;
  }, []);

  const handleAccordionChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const resetAllExcept = (section: string) => {
    if (section === activeSection) return;
    setActiveSection(section);
    if (section !== 'minutes')
      setMinutesState({
        startMinute: '00',
        calTime: '00',
        every: '30',
        from: { hour: '10', minute: '00', ampm: 'AM' },
        to: { hour: '6', minute: '00', ampm: 'PM' },
        days: [],
      });
    if (section !== 'hourly') {
      setHourlyState({
        startMinute: '00',
        every: '2',
        from: { hour: '10', minute: '00', ampm: 'AM' },
        to: { hour: '6', minute: '00', ampm: 'PM' },
        days: [],
      });
    }
    if (section !== 'daily')
      setDailyState({ at: { hour: '8', minute: '30', ampm: 'AM' }, days: [] });
    if (section !== 'weekly')
      setWeeklyState({
        at: { hour: '8', minute: '30', ampm: 'AM' },
        days: [],
        alternateWeeks: false,
      });
    if (section !== 'monthly')
      setMonthlyState({
        at: { hour: '8', minute: '30', ampm: 'AM' },
        mode: 'day',
        day: '1',
        nth: 'First',
        weekday: 'Mon',
        everyNMonths: '1',
      });
  };

  const handleDayToggle = (day: string, section: 'minutes' | 'hourly' | 'daily' | 'weekly') => {
    if (section === 'minutes') {
      setMinutesState((prev) => ({
        ...prev,
        days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day],
      }));
      resetAllExcept('minutes');
    } else if (section === 'hourly') {
      setHourlyState((prev) => ({
        ...prev,
        days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day],
      }));
      resetAllExcept('hourly');
    } else if (section === 'daily') {
      setDailyState((prev) => ({
        ...prev,
        days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day],
      }));
      resetAllExcept('daily');
    } else if (section === 'weekly') {
      setWeeklyState((prev) => ({
        ...prev,
        days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day],
      }));
      resetAllExcept('weekly');
    }
  };

  const resetForm = () => {
    setScheduledName('');
    setActiveSection('daily');
    setExpanded('minutes');
    setMinutesState({
      startMinute: '00',
      calTime: '00',
      every: '30',
      from: { hour: '10', minute: '00', ampm: 'AM' },
      to: { hour: '6', minute: '00', ampm: 'PM' },
      days: [],
    });
    setHourlyState({
      startMinute: '00',
      every: '2',
      from: { hour: '10', minute: '00', ampm: 'AM' },
      to: { hour: '6', minute: '00', ampm: 'PM' },
      days: [],
    });
    setDailyState({ at: { hour: '8', minute: '30', ampm: 'AM' }, days: [] });
    setWeeklyState({
      at: { hour: '8', minute: '30', ampm: 'AM' },
      days: [],
      alternateWeeks: false,
    });
    setMonthlyState({
      at: { hour: '8', minute: '30', ampm: 'AM' },
      mode: 'day',
      day: '1',
      nth: 'First',
      weekday: 'Mon',
      everyNMonths: '1',
    });
    setSelectedSchedule(null);
  };

  const handleScheduleRefresh = async () => {
    let payload: any = {};

    if (['minutes', 'hourly'].includes(activeSection)) {
      const state = activeSection === 'minutes' ? minutesState : hourlyState;
      const fromHour = parseInt(state.from.hour, 10);
      const toHour = parseInt(state.to.hour, 10);
      const fromAMPM = state.from.ampm;
      const toAMPM = state.to.ampm;

      const from24 =
        fromAMPM === 'PM' && fromHour !== 12
          ? fromHour + 12
          : fromAMPM === 'AM' && fromHour === 12
            ? 0
            : fromHour;
      const to24 =
        toAMPM === 'PM' && toHour !== 12
          ? toHour + 12
          : toAMPM === 'AM' && toHour === 12
            ? 0
            : toHour;

      if (to24 < from24 || (to24 === from24 && activeSection === 'minutes')) {
        setAlert({
          severity: 'warning',
          message: 'From and To time should be within the same day.',
          show: true,
        });
        return;
      }
    }

    switch (activeSection) {
      case 'minutes':
        payload = {
          startMinute: parseInt(minutesState.startMinute),
          time: parseInt(minutesState.every),
          type: 'minutes',
          from: minutesState.from,
          to: minutesState.to,
          days: minutesState.days.map((d) => d.toLowerCase()),
          name: scheduledName,
        };
        break;
      case 'hourly':
        payload = {
          startMinute: parseInt(hourlyState.startMinute),
          time: parseInt(hourlyState.every),
          type: 'hours',
          from: hourlyState.from,
          to: hourlyState.to,
          days: hourlyState.days.map((d) => d.toLowerCase()),
          name: scheduledName,
        };
        break;
      case 'daily':
        payload = {
          type: 'daily',
          at: dailyState.at,
          days: dailyState.days.map((d) => d.toLowerCase()),
          name: scheduledName,
        };
        break;
      case 'weekly':
        payload = {
          type: 'weeks',
          at: weeklyState.at,
          days: weeklyState.days.map((d) => d.toLowerCase()),
          alternateWeeks: weeklyState.alternateWeeks,
          name: scheduledName,
        };
        break;
      case 'monthly':
        payload = {
          type: 'month',
          at: monthlyState.at,
          mode: monthlyState.mode,
          day: parseInt(monthlyState.day),
          nth: monthlyState.nth.toLowerCase(),
          weekday: monthlyState.weekday,
          everyNMonths: parseInt(monthlyState.everyNMonths),
          name: scheduledName,
        };
        break;
    }
    // console.log('Payload:', payload);

    if (scheduledName.trim().length < 2) {
      setAlert({
        severity: 'error',
        message: 'Schedule name must be at least 3 characters',
        show: true,
      });
      return;
    }
    if (scheduledName.length > 40) {
      setAlert({
        severity: 'error',
        message: 'Schedule name cannot exceed 40 characters',
        show: true,
      });
      return;
    }
    if (!isNameAllowed(scheduledName)) {
      setAlert({
        severity: 'error',
        message:
          'Schedule name can only contain letters, numbers, spaces, hyphens, and underscores',
        show: true,
      });
      return;
    }
    const existingScheduleName = schedules.find((schedule: IDatasetSchedule) =>
      schedule.name.includes(scheduledName),
    );
    if (existingScheduleName) {
      setAlert({
        severity: 'error',
        message: 'Schedule name already exists',
        show: true,
      });
      return;
    }

    if (!selectedDataset) return;
    if (schedules.length >= 3) {
      setAlert({
        severity: 'error',
        message: 'You can only create up to 3 schedules per dataset.',
        show: true,
      });
      setTimeout(() => {
        resetForm();
        handleClose();
      }, 2000);
      return;
    }
    const result = createDatasetSchedule(
      selectedDataset.datasetId,
      selectedDataset.workspaceId,
      payload,
    );
    const { message, isError } = await result;

    setAlert({
      severity: isError ? 'error' : 'success',
      message: message,
      show: true,
    });
    if (!isError) {
      setTimeout(() => {
        fetchDatasetSchedule();
        resetForm();
        handleClose();
      }, 2000);
      return;
    }
  };

  const handleClose = () => {
    setSelectedSchedule(null);
    setOpen(false);
    setViewSchedule(false);
  };

  useEffect(() => {
    if (selectedSchedule?.scheduleJson && selectedSchedule) {
      setExpanded(
        selectedSchedule.type === 'hours'
          ? 'hourly'
          : selectedSchedule.type === 'month'
            ? 'monthly'
            : selectedSchedule.type === 'weeks'
              ? 'weekly'
              : selectedSchedule.type,
      );
      const sched = selectedSchedule.scheduleJson;
      setScheduledName(selectedSchedule.name || '');
      setActiveSection(
        selectedSchedule.type === 'hours'
          ? 'hourly'
          : selectedSchedule.type === 'month'
            ? 'monthly'
            : selectedSchedule.type === 'weeks'
              ? 'weekly'
              : selectedSchedule.type,
      );

      if (sched.type === 'minutes') {
        setMinutesState({
          startMinute: sched.startMinute?.toString().padStart(2, '0') || '00',
          calTime:
            Number(sched.startMinute) < 30
              ? (Number(sched.startMinute) + Number(sched.time || sched.time || 30))
                  .toString()
                  .padStart(2, '0')
              : (Number(sched.startMinute) - Number(sched.time || sched.time || 30))
                  .toString()
                  .padStart(2, '0'),
          every: sched.time?.toString() || '30',
          from: {
            hour: sched.from?.hour?.toString() || '12',
            minute: sched.from?.minute?.toString().padStart(2, '0') || '00',
            ampm: sched.from?.ampm || 'AM',
          },
          to: {
            hour: sched.to?.hour?.toString() || '12',
            minute: sched.to?.minute?.toString().padStart(2, '0') || '00',
            ampm: sched.to?.ampm || 'PM',
          },
          days: (sched.days || []).map((d) => d.charAt(0).toUpperCase() + d.slice(1, 3)),
        });
      } else if (sched.type === 'hours') {
        setHourlyState({
          startMinute: sched.startMinute?.toString().padStart(2, '0') || '00',
          every: sched.time?.toString() || '2',
          from: {
            hour: sched.from?.hour?.toString() || '9',
            minute: sched.from?.minute?.toString().padStart(2, '0') || '00',
            ampm: sched.from?.ampm || 'AM',
          },
          to: {
            hour: sched.to?.hour?.toString() || '5',
            minute: sched.to?.minute?.toString().padStart(2, '0') || '00',
            ampm: sched.to?.ampm || 'PM',
          },
          days: (sched.days || []).map((d) => d.charAt(0).toUpperCase() + d.slice(1, 3)),
        });
      } else if (sched.type === 'daily') {
        setDailyState({
          at: {
            hour: sched.at?.hour?.toString() || '8',
            minute: sched.at?.minute?.toString().padStart(2, '0') || '30',
            ampm: sched.at?.ampm || 'AM',
          },
          days: (sched.days || []).map((d) => d.charAt(0).toUpperCase() + d.slice(1, 3)),
        });
      } else if (sched.type === 'weeks') {
        setWeeklyState({
          at: {
            hour: sched.at?.hour?.toString() || '8',
            minute: sched.at?.minute?.toString().padStart(2, '0') || '30',
            ampm: sched.at?.ampm || 'AM',
          },
          days: (sched.days || []).map((d) => d.charAt(0).toUpperCase() + d.slice(1, 3)),
          alternateWeeks: !!sched.alternateWeeks,
        });
      } else if (sched.type === 'month') {
        setMonthlyState({
          at: {
            hour: sched.at?.hour?.toString() || '8',
            minute: sched.at?.minute?.toString().padStart(2, '0') || '30',
            ampm: sched.at?.ampm || 'AM',
          },
          mode: sched.mode === 'nthWeekday' ? 'nthWeekday' : sched.mode || 'day',
          day: sched.day?.toString() || '1',
          nth: sched.nth ? sched.nth.charAt(0).toUpperCase() + sched.nth.slice(1) : 'First',
          weekday: sched.weekday || 'Mon',
          everyNMonths: sched.everyNMonths?.toString() || '1',
        });
      }
    }
  }, [selectedSchedule]);

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="sm">
        <DialogTitle
          display={'flex'}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 0,
            pr: 0,
            pl: 2.6,
            m: 0,
          }}
        >
          <Typography
            variant="h4"
            component="div"
            sx={{ fontWeight: 600, fontSize: fontSize.doubleExtraLarge }}
          >
            {selectedSchedule ? 'View Dataset Refresh Schedule' : 'Create Dataset Refresh Schedule'}
          </Typography>
          <DialogActions sx={{ mr: 2.8 }}>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </DialogActions>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Accordion expanded={expanded === 'minutes'} onChange={handleAccordionChange('minutes')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    fontSize: fontSize.large,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  Minutes Basis
                  {activeSection === 'minutes' && (
                    <CheckCircleIcon sx={{ color: '#2bb9bb', fontSize: fontSize.large }} />
                  )}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                display="flex"
                flexDirection="column"
                gap={3}
                sx={{ width: 'fit-content', alignItems: 'flex-start' }}
              >
                <Box display="flex" alignItems="start" gap={1.5} flexWrap="wrap">
                  <Typography fontWeight={600} sx={{ minWidth: 80 }}>
                    Minutes <span style={{ color: '#404040' }}>*</span>
                  </Typography>
                  <Box display="flex" flexDirection="column" ml={-0.3} gap={2} flexWrap="wrap">
                    {/* Every */}
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography sx={{ minWidth: 40, fontWeight: 500 }}>Every</Typography>
                      <Select
                        size="small"
                        value={minutesState.every}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setMinutesState((prev) => ({ ...prev, every: e.target.value }));
                          resetAllExcept('minutes');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        <MenuItem value={30}>30</MenuItem>
                      </Select>
                      <Typography sx={{ minWidth: 55 }}>minute(s)</Typography>
                    </Box>
                    {/* Start */}
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography sx={{ minWidth: 40, fontWeight: 500 }}>Start</Typography>
                        <Select
                          size="small"
                          value={minutesState.startMinute}
                          readOnly={viewSchedule}
                          onChange={(e) => {
                            setMinutesState((prev) => {
                              const start = Number(e.target.value);
                              const every = Number(minutesState.every);
                              let calTime;
                              if (start < 30) {
                                calTime = (start + every).toString();
                              } else {
                                calTime = (start - every).toString();
                                if (Number(calTime) < 0) calTime = '00';
                              }
                              return {
                                ...prev,
                                startMinute: e.target.value,
                                calTime,
                              };
                            });
                            resetAllExcept('minutes');
                          }}
                          sx={selectStyle}
                          MenuProps={{
                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                overflowY: 'auto',
                              },
                            },
                          }}
                        >
                          {Array.from({ length: 60 }, (_, i) => (
                            <MenuItem key={i} value={i < 10 ? `0${i}` : `${i}`}>
                              {i < 10 ? `0${i}` : `${i}`}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography>minute(s)</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography sx={{ minWidth: 20 }}>and</Typography>
                        <Select
                          size="small"
                          value={minutesState.calTime}
                          readOnly
                          sx={selectStyle}
                          MenuProps={{
                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                overflowY: 'auto',
                              },
                            },
                          }}
                        >
                          <MenuItem value={minutesState.calTime}>
                            {minutesState.calTime.padStart(2, '0')}
                          </MenuItem>
                        </Select>
                        <Typography sx={{ minWidth: 55 }}>minute(s)</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  ml={11.3}
                  mt={-1}
                  alignItems="flex-start"
                >
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography sx={{ minWidth: 40 }}>From</Typography>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Select
                        size="small"
                        value={minutesState.from.hour}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setMinutesState((prev) => ({
                            ...prev,
                            from: { ...prev.from, hour: e.target.value },
                          }));
                          resetAllExcept('minutes');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              height: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {hours.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                      <Select
                        size="small"
                        value={minutesState.from.ampm}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setMinutesState((prev) => ({
                            ...prev,
                            from: { ...prev.from, ampm: e.target.value },
                          }));
                          resetAllExcept('minutes');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        <MenuItem value={'AM'}>AM</MenuItem>
                        <MenuItem value={'PM'}>PM</MenuItem>
                      </Select>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography sx={{ minWidth: 40 }}>To</Typography>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Select
                        size="small"
                        value={minutesState.to.hour}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setMinutesState((prev) => ({
                            ...prev,
                            to: { ...prev.to, hour: e.target.value },
                          }));
                          resetAllExcept('minutes');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              height: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {hours.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                      <Select
                        size="small"
                        value={minutesState.to.ampm}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setMinutesState((prev) => ({
                            ...prev,
                            to: { ...prev.to, ampm: e.target.value },
                          }));
                          resetAllExcept('minutes');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        <MenuItem value={'AM'}>AM</MenuItem>
                        <MenuItem value={'PM'}>PM</MenuItem>
                      </Select>
                    </Box>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    fontWeight={600}
                    sx={{ minWidth: 80, display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    Days
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip
                        title="If none is selected, all days are considered."
                        arrow
                        placement="top"
                      >
                        <IconButton size="small" sx={{ p: 0.2 }}>
                          <InfoOutline
                            sx={{
                              fontSize: '12px',
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Typography>
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.4}>
                    {days.map((day) => (
                      <ToggleButton
                        key={day}
                        value={day}
                        selected={minutesState.days.includes(day)}
                        onChange={() => handleDayToggle(day, 'minutes')}
                        size="small"
                        disabled={viewSchedule}
                        sx={{
                          '&.Mui-selected': {
                            border: '1px solid #2bb9bb',
                            color: '#2bb9bb',
                            backgroundColor: 'transparent',
                            '&:hover': {
                              borderColor: '#14a1a3ff',
                              backgroundColor: 'rgba(43,185,187,0.08)',
                            },
                          },
                        }}
                      >
                        {day}
                      </ToggleButton>
                    ))}
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'hourly'} onChange={handleAccordionChange('hourly')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    fontSize: fontSize.large,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  Hourly
                  {activeSection === 'hourly' && (
                    <CheckCircleIcon sx={{ color: '#2bb9bb', fontSize: fontSize.large }} />
                  )}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2} sx={{ width: 'fit-content' }}>
                <Box display="flex" alignItems="baseline" gap={5.8} flexWrap="wrap">
                  <Typography fontWeight={600}>
                    Hourly <span style={{ color: '#404040' }}>*</span>
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2} flexWrap="wrap">
                    {/* Every */}
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography sx={{ minWidth: 40, fontWeight: 500 }}>Every</Typography>
                      <Select
                        size="small"
                        value={hourlyState.every}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setHourlyState((prev) => ({ ...prev, every: e.target.value }));
                          resetAllExcept('hourly');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {everyhour.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography sx={{ minWidth: 55 }}>hour(s)</Typography>
                    </Box>
                    {/* Start */}
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography sx={{ minWidth: 40, fontWeight: 500 }}>Start</Typography>
                      <Select
                        size="small"
                        value={hourlyState.startMinute}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setHourlyState((prev) => ({ ...prev, startMinute: e.target.value }));
                          resetAllExcept('hourly');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              maxHeight: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {Array.from({ length: 60 }, (_, i) => (
                          <MenuItem key={i} value={i < 10 ? `0${i}` : `${i}`}>
                            {i < 10 ? `0${i}` : `${i}`}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography sx={{ minWidth: 55 }}>minute(s)</Typography>
                    </Box>
                  </Box>
                </Box>

                <Box display="flex" flexDirection="column" gap={2} ml={11.3}>
                  <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
                    <Typography sx={{ minWidth: 40 }}>From</Typography>
                    <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
                      <Select
                        size="small"
                        value={hourlyState.from.hour}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setHourlyState((prev) => ({
                            ...prev,
                            from: { ...prev.from, hour: e.target.value },
                          }));
                          resetAllExcept('hourly');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              height: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {hours.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                      <Select
                        size="small"
                        value={hourlyState.from.ampm}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setHourlyState((prev) => ({
                            ...prev,
                            from: { ...prev.from, ampm: e.target.value },
                          }));
                          resetAllExcept('hourly');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        <MenuItem value={'AM'}>AM</MenuItem>
                        <MenuItem value={'PM'}>PM</MenuItem>
                      </Select>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
                    <Typography sx={{ minWidth: 40 }}>To</Typography>
                    <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
                      <Select
                        size="small"
                        value={hourlyState.to.hour}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setHourlyState((prev) => ({
                            ...prev,
                            to: { ...prev.to, hour: e.target.value },
                          }));
                          resetAllExcept('hourly');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              height: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {hours.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                      <Select
                        size="small"
                        value={hourlyState.to.ampm}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setHourlyState((prev) => ({
                            ...prev,
                            to: { ...prev.to, ampm: e.target.value },
                          }));
                          resetAllExcept('hourly');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        <MenuItem value={'AM'}>AM</MenuItem>
                        <MenuItem value={'PM'}>PM</MenuItem>
                      </Select>
                    </Box>
                  </Box>
                </Box>

                <Box display="flex" alignItems="baseline" gap={5.8} flexWrap="wrap">
                  <Typography
                    fontWeight={600}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    Days{' '}
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip
                        title="If none is selected, all days are considered."
                        arrow
                        placement="top"
                      >
                        <IconButton size="small" sx={{ p: 0.2 }}>
                          <InfoOutline
                            sx={{
                              fontSize: '12px',
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Typography>
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.35}>
                    {days.map((day) => (
                      <ToggleButton
                        key={day}
                        value={day}
                        selected={hourlyState.days.includes(day)}
                        onChange={() => handleDayToggle(day, 'hourly')}
                        size="small"
                        disabled={viewSchedule}
                        sx={{
                          '&.Mui-selected': {
                            border: '1px solid #2bb9bb',
                            color: '#2bb9bb',
                            backgroundColor: 'transparent',
                            '&:hover': {
                              borderColor: '#14a1a3ff',
                              backgroundColor: 'rgba(43,185,187,0.08)',
                            },
                          },
                        }}
                      >
                        {day}
                      </ToggleButton>
                    ))}
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'daily'} onChange={handleAccordionChange('daily')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    fontSize: fontSize.large,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  Daily
                  {activeSection === 'daily' && (
                    <CheckCircleIcon sx={{ color: '#2bb9bb', fontSize: fontSize.large }} />
                  )}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={3} sx={{ width: 'fit-content' }}>
                <Box display="flex" alignItems="center" gap={7} flexWrap="wrap">
                  <Typography fontWeight={600}>
                    Daily <span style={{ color: '#404040' }}>*</span>
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <Typography sx={{ minWidth: 20 }}>At</Typography>
                    <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
                      <Select
                        size="small"
                        value={dailyState.at.hour}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setDailyState((prev) => ({
                            ...prev,
                            at: { ...prev.at, hour: e.target.value },
                          }));
                          resetAllExcept('daily');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              height: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {hours.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                      <Select
                        size="small"
                        value={dailyState.at.minute}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setDailyState((prev) => ({
                            ...prev,
                            at: { ...prev.at, minute: e.target.value },
                          }));
                          resetAllExcept('daily');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              height: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {minutes.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                      <Select
                        size="small"
                        value={dailyState.at.ampm}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setDailyState((prev) => ({
                            ...prev,
                            at: { ...prev.at, ampm: e.target.value },
                          }));
                          resetAllExcept('daily');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        <MenuItem value={'AM'}>AM</MenuItem>
                        <MenuItem value={'PM'}>PM</MenuItem>
                      </Select>
                    </Box>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={5.8} flexWrap="wrap">
                  <Typography
                    fontWeight={600}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    Days{' '}
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip
                        title="If none is selected, all days are considered."
                        arrow
                        placement="top"
                      >
                        <IconButton size="small" sx={{ p: 0.2 }}>
                          <InfoOutline
                            sx={{
                              fontSize: '12px',
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Typography>
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.04}>
                    {days.map((day) => (
                      <ToggleButton
                        key={day}
                        value={day}
                        selected={dailyState.days.includes(day)}
                        disabled={viewSchedule}
                        onChange={() => handleDayToggle(day, 'daily')}
                        size="small"
                        sx={{
                          '&.Mui-selected': {
                            border: '1px solid #2bb9bb',
                            color: '#2bb9bb',
                            backgroundColor: 'transparent',
                            '&:hover': {
                              borderColor: '#14a1a3ff',
                              backgroundColor: 'rgba(43,185,187,0.08)',
                            },
                          },
                        }}
                      >
                        {day}
                      </ToggleButton>
                    ))}
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'weekly'} onChange={handleAccordionChange('weekly')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    fontSize: fontSize.large,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  Weekly
                  {activeSection === 'weekly' && (
                    <CheckCircleIcon sx={{ color: '#2bb9bb', fontSize: fontSize.large }} />
                  )}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={3} sx={{ width: 'fit-content' }}>
                <Box display="flex" alignItems="center" gap={6} flexWrap="wrap">
                  <Typography fontWeight={600}>
                    Weekly <span style={{ color: '#404040' }}>*</span>
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <Typography sx={{ minWidth: 20 }}>At</Typography>
                    <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
                      <Select
                        size="small"
                        value={weeklyState.at.hour}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setWeeklyState((prev) => ({
                            ...prev,
                            at: { ...prev.at, hour: e.target.value },
                          }));
                          resetAllExcept('weekly');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              height: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {hours.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                      <Select
                        size="small"
                        value={weeklyState.at.minute}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setWeeklyState((prev) => ({
                            ...prev,
                            at: { ...prev.at, minute: e.target.value },
                          }));
                          resetAllExcept('weekly');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              height: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {minutes.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                      <Select
                        size="small"
                        value={weeklyState.at.ampm}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setWeeklyState((prev) => ({
                            ...prev,
                            at: { ...prev.at, ampm: e.target.value },
                          }));
                          resetAllExcept('weekly');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        <MenuItem value={'AM'}>AM</MenuItem>
                        <MenuItem value={'PM'}>PM</MenuItem>
                      </Select>
                    </Box>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={6} flexWrap="wrap">
                  <Typography
                    fontWeight={600}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    Days{' '}
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip
                        title="If none is selected, all days are considered."
                        arrow
                        placement="top"
                      >
                        <IconButton size="small" sx={{ p: 0.2 }}>
                          <InfoOutline
                            sx={{
                              fontSize: '12px',
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Typography>
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.2}>
                    {days.map((day) => (
                      <ToggleButton
                        key={day}
                        value={day}
                        disabled={viewSchedule}
                        selected={weeklyState.days.includes(day)}
                        onChange={() => handleDayToggle(day, 'weekly')}
                        size="small"
                        sx={{
                          '&.Mui-selected': {
                            border: '1px solid #2bb9bb',
                            color: '#2bb9bb',
                            backgroundColor: 'transparent',
                            '&:hover': {
                              borderColor: '#14a1a3ff',
                              backgroundColor: 'rgba(43,185,187,0.08)',
                            },
                          },
                        }}
                      >
                        {day}
                      </ToggleButton>
                    ))}
                  </Box>
                </Box>
                <FormControlLabel
                  disabled={viewSchedule}
                  control={
                    <Checkbox
                      checked={weeklyState.alternateWeeks}
                      onChange={(e) => {
                        setWeeklyState((prev) => ({ ...prev, alternateWeeks: e.target.checked }));
                        resetAllExcept('weekly');
                      }}
                    />
                  }
                  label="Alternate weeks"
                  sx={{ ml: 10.4 }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'monthly'} onChange={handleAccordionChange('monthly')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    fontSize: fontSize.large,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  Monthly
                  {activeSection === 'monthly' && (
                    <CheckCircleIcon sx={{ color: '#2bb9bb', fontSize: fontSize.large }} />
                  )}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={3} sx={{ width: 'fit-content' }}>
                <Box display="flex" alignItems="center" gap={7} flexWrap="wrap">
                  <Typography fontWeight={600}>
                    Monthly <span style={{ color: '#404040' }}>*</span>
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <Typography>At</Typography>
                    <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
                      <Select
                        size="small"
                        value={monthlyState.at.hour}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setMonthlyState((prev) => ({
                            ...prev,
                            at: { ...prev.at, hour: e.target.value },
                          }));
                          resetAllExcept('monthly');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              height: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {hours.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                      <Select
                        size="small"
                        value={monthlyState.at.minute}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setMonthlyState((prev) => ({
                            ...prev,
                            at: { ...prev.at, minute: e.target.value },
                          }));
                          resetAllExcept('monthly');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              height: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {minutes.map((h) => (
                          <MenuItem key={h} value={h}>
                            {h}
                          </MenuItem>
                        ))}
                      </Select>
                      <Select
                        size="small"
                        value={monthlyState.at.ampm}
                        readOnly={viewSchedule}
                        onChange={(e) => {
                          setMonthlyState((prev) => ({
                            ...prev,
                            at: { ...prev.at, ampm: e.target.value },
                          }));
                          resetAllExcept('monthly');
                        }}
                        sx={selectStyle}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        <MenuItem value={'AM'}>AM</MenuItem>
                        <MenuItem value={'PM'}>PM</MenuItem>
                      </Select>
                    </Box>
                  </Box>
                </Box>

                <FormControl
                  sx={{
                    gap: 2,
                  }}
                >
                  <RadioGroup
                    value={monthlyState.mode}
                    onChange={(e) => {
                      setMonthlyState((prev) => ({
                        ...prev,
                        mode: e.target.value as 'day' | 'nthWeekday',
                      }));
                      resetAllExcept('monthly');
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      ml: 1.3,
                    }}
                  >
                    <FormControlLabel
                      value="day"
                      disabled={viewSchedule}
                      control={<Radio readOnly={viewSchedule} />}
                      label={
                        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                          <Typography>Day</Typography>
                          <Select
                            size="small"
                            value={monthlyState.day}
                            readOnly={viewSchedule}
                            onChange={(e) => {
                              setMonthlyState((prev) => ({
                                ...prev,
                                day: e.target.value,
                                mode: 'day',
                              }));
                              resetAllExcept('monthly');
                            }}
                            sx={selectStyle}
                            MenuProps={{
                              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                              transformOrigin: { vertical: 'top', horizontal: 'left' },
                              PaperProps: {
                                sx: {
                                  height: 200,
                                  overflowY: 'auto',
                                },
                              },
                            }}
                          >
                            {Array.from({ length: 31 }, (_, i) => (
                              <MenuItem key={i + 1} value={i + 1}>
                                {i + 1}
                              </MenuItem>
                            ))}
                          </Select>
                          <Typography>of every</Typography>
                          <Select
                            size="small"
                            value={monthlyState.everyNMonths}
                            readOnly={viewSchedule}
                            onChange={(e) => {
                              setMonthlyState((prev) => ({
                                ...prev,
                                everyNMonths: e.target.value,
                                mode: 'day',
                              }));
                              resetAllExcept('monthly');
                            }}
                            sx={selectStyle}
                            MenuProps={{
                              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                              transformOrigin: { vertical: 'top', horizontal: 'left' },
                              PaperProps: {
                                sx: {
                                  height: 200,
                                  overflowY: 'auto',
                                },
                              },
                            }}
                          >
                            {Array.from({ length: 12 }, (_, i) => (
                              <MenuItem key={i + 1} value={i + 1}>
                                {i + 1}
                              </MenuItem>
                            ))}
                          </Select>
                          <Typography>month(s)</Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                  <FormControl>
                    <RadioGroup
                      value={monthlyState.mode}
                      onChange={(e) => {
                        setMonthlyState((prev) => ({
                          ...prev,
                          mode: e.target.value as 'day' | 'nthWeekday',
                        }));
                        resetAllExcept('monthly');
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        ml: 1.3,
                      }}
                    >
                      <FormControlLabel
                        disabled={viewSchedule}
                        value="nthWeekday"
                        control={<Radio readOnly={viewSchedule} />}
                        label={
                          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                            <Typography>The</Typography>
                            <Select
                              size="small"
                              value={monthlyState.nth}
                              readOnly={viewSchedule}
                              onChange={(e) => {
                                setMonthlyState((prev) => ({
                                  ...prev,
                                  nth: e.target.value,
                                  mode: 'nthWeekday',
                                }));
                                resetAllExcept('monthly');
                              }}
                              sx={selectStyle}
                            >
                              {ordinals.map((o) => (
                                <MenuItem key={o} value={o}>
                                  {o}
                                </MenuItem>
                              ))}
                            </Select>
                            <Select
                              size="small"
                              value={monthlyState.weekday}
                              readOnly={viewSchedule}
                              onChange={(e) => {
                                setMonthlyState((prev) => ({
                                  ...prev,
                                  weekday: e.target.value,
                                  mode: 'nthWeekday',
                                }));
                                resetAllExcept('monthly');
                              }}
                              sx={selectStyle}
                              MenuProps={{
                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                transformOrigin: { vertical: 'top', horizontal: 'left' },
                                PaperProps: {
                                  sx: {
                                    height: 200,
                                    overflowY: 'auto',
                                  },
                                },
                              }}
                            >
                              {days.map((d) => (
                                <MenuItem key={d} value={d}>
                                  {d}
                                </MenuItem>
                              ))}
                            </Select>
                            <Typography>of every</Typography>
                            <Select
                              size="small"
                              value={monthlyState.everyNMonths}
                              readOnly={viewSchedule}
                              onChange={(e) => {
                                setMonthlyState((prev) => ({
                                  ...prev,
                                  everyNMonths: e.target.value,
                                  mode: 'nthWeekday',
                                }));
                                resetAllExcept('monthly');
                              }}
                              sx={selectStyle}
                              MenuProps={{
                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                transformOrigin: { vertical: 'top', horizontal: 'left' },
                                PaperProps: {
                                  sx: {
                                    height: 200,
                                    overflowY: 'auto',
                                  },
                                },
                              }}
                            >
                              {Array.from({ length: 12 }, (_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>
                                  {i + 1}
                                </MenuItem>
                              ))}
                            </Select>
                            <Typography>month(s)</Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </FormControl>
              </Box>
            </AccordionDetails>
          </Accordion>

          <DialogActions sx={{ display: 'flex', justifyContent: 'end', margin: 0, p: 0, py: 1 }}>
            <TextField
              label="Timezone"
              value={timezoneDisplay}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                width: 228,
                '& .MuiOutlinedInput-root': {
                  height: 30,
                  margin: 0,
                  px: 0,
                  py: -1,
                  fontSize: fontSize.medium,
                },
              }}
              InputLabelProps={{
                sx: {
                  transform: 'translate(14px, 10px) scale(1)',
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                  },
                },
              }}
            />
          </DialogActions>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
          }}
        >
          <Box
            display={'flex'}
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: 0,
              m: 0,
              pr: 2,
            }}
          >
            {!viewSchedule && (
              <Button
                variant="outlined"
                sx={{
                  color: '#404040',
                  borderColor: ' #404040',
                }}
                onClick={resetForm}
              >
                Reset
              </Button>
            )}
          </Box>
          <Box
            display={'flex'}
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{
              p: 0,
              m: 0,
            }}
          >
            <TextField
              type="text"
              placeholder="Schedule Name *"
              variant="outlined"
              size="small"
              value={scheduledName}
              onChange={(e) => {
                setScheduledName(e.target.value);
              }}
              InputProps={{
                readOnly: viewSchedule,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: fontSize.small,
                  height: 30,
                  width: viewSchedule ? 228 : '100%',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '2px 10px',
                },
              }}
            />
            {!viewSchedule && (
              <Button
                variant="contained"
                onClick={handleScheduleRefresh}
                sx={{
                  backgroundColor: '#2bb9bb',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#14a1a3ff' },
                }}
              >
                Save
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {alert && alert.show && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert.show}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
    </>
  );
}
